import type { TimelineEvent } from "@productlane/api/src/browser"
import type { AiResolutionChange } from "@productlane/lib"
import { AI_BUDGET_REACHED_MESSAGE } from "@productlane/lib"
import { parseThreadActivity } from "@productlane/lib/src/browser"

import { resolveActivityActorName } from "./activity-actor-name"
import { connectorRunPurpose } from "./agent-burst-summary"
import { getAiChatFeedbackComment } from "./ai-chat-feedback-comment"
import { parseLiveChatSystemMessage } from "./live-chat-system-message"

/**
 * Returns a short summary string for a system event,
 * used in the collapsed activity run summary row.
 */

const AI_RESOLUTION_SUMMARY: Record<AiResolutionChange, string> = {
	judged_resolved: "counted as resolved by the agent",
	judged_escalated: "counted as escalated",
	assumed_resolved: "counted as resolved after 24h quiet",
	revoked: "no longer counted as resolved",
}

export function getSystemEventSummaryText(event: TimelineEvent): string {
	switch (event.type) {
		case "activity": {
			const { threadActivity } = event
			const actorName = resolveActivityActorName(
				threadActivity.actorName,
				threadActivity.payload,
			)
			try {
				const parsed = parseThreadActivity(
					threadActivity.eventType,
					threadActivity.payload,
				)
				return getActivitySummary(parsed, actorName)
			} catch {
				return "activity"
			}
		}
		case "createdAt":
			return event.thread.creatorName
				? `${event.thread.creatorName} created this thread`
				: "thread created"
		case "agentActivityBurst":
			return `Productlane Agent ran ${event.burst.events.length} actions`
		case "routineRun":
			return `Routine made ${event.run.events.length} changes`
		case "comment": {
			const feedback = getAiChatFeedbackComment(event)
			if (!feedback) return "device info"
			return feedback.sentiment === "positive"
				? "customer liked the reply"
				: "customer disliked the reply"
		}
		case "live-chat-message": {
			const parsed = parseLiveChatSystemMessage(event.liveChatMessage.content)
			if (parsed.kind === "notice") return parsed.text
			return `${parsed.name} ${parsed.kind === "joined" ? "joined" : "ended the conversation"}`
		}
		default:
			return "event"
	}
}

function getActivitySummary(
	parsed: ReturnType<typeof parseThreadActivity>,
	actorName: string,
): string {
	switch (parsed.eventType) {
		case "thread_snoozed":
			return "snoozed"
		case "thread_unsnoozed":
			return "unsnoozed"
		case "thread_marked_done":
			if (parsed.payload.source === "linear_asks") {
				return `marked done with ${parsed.payload.issueIdentifier ?? "the Ask"}`
			}
			return parsed.payload.source === "ai_agent"
				? "AI agent marked done"
				: "marked done"
		case "thread_reopened":
			switch (parsed.payload.source) {
				case "customer_reply":
					return "reopened by a customer reply"
				case "ai_handoff":
					return "reopened for a handoff"
				case "ai_notified":
					return "reopened for a report"
				case "email_bounce":
					return "reopened because the reply bounced"
				case "unanswered_message":
					return "reopened for an unanswered message"
				default:
					return "reopened"
			}
		case "thread_merged": {
			const source = parsed.payload.sourceTitle?.trim() || "thread"
			const target = parsed.payload.targetTitle?.trim() || "thread"
			return `merged ${source} into ${target}`
		}
		case "thread_split": {
			const count = parsed.payload.messageCount
			return `moved ${count} message${count === 1 ? "" : "s"} to ${parsed.payload.newTitle}`
		}
		case "assignee_changed":
			return "assignee changed"
		case "self_assigned":
			return `${actorName} self-assigned`
		case "contact_changed":
			return parsed.payload.newContactName
				? `contact → ${parsed.payload.newContactName}`
				: "contact removed"
		case "company_changed":
			return parsed.payload.newCompanyName
				? `company → ${parsed.payload.newCompanyName}`
				: "company removed"
		case "auto_assigned":
			return "auto-assigned"
		case "issue_linked":
			return parsed.payload.linkedVia === "linear_asks"
				? `Linear Asks linked ${parsed.payload.issueIdentifier ?? "issue"}`
				: `linked ${parsed.payload.issueIdentifier ?? "issue"}`
		case "issue_unlinked":
			return `unlinked ${parsed.payload.issueIdentifier ?? "issue"}`
		case "issue_created":
			return `issue ${parsed.payload.issueIdentifier ?? ""} created`.trim()
		case "project_linked":
			return `linked project ${parsed.payload.projectName ?? ""}`.trim()
		case "project_unlinked":
			return `unlinked project ${parsed.payload.projectName ?? ""}`.trim()
		case "tag_linked":
			return `added tag ${parsed.payload.tagName ?? ""}`.trim()
		case "tag_unlinked":
			return `removed tag ${parsed.payload.tagName ?? ""}`.trim()
		case "priority_changed":
			return "priority changed"
		case "status_changed":
			return `status → ${parsed.payload.newStatus}`
		case "linked_issue_planned":
			return `${parsed.payload.issueIdentifier ?? "issue"} planned`
		case "linked_issue_started":
			return `${parsed.payload.issueIdentifier ?? "issue"} in progress`
		case "linked_issue_completed":
			return `${parsed.payload.issueIdentifier ?? "issue"} completed`
		case "linked_issue_cancelled":
			return `${parsed.payload.issueIdentifier ?? "issue"} cancelled`
		case "linked_project_completed":
			return `${parsed.payload.projectName ?? "project"} completed`
		case "linked_project_cancelled":
			return `${parsed.payload.projectName ?? "project"} cancelled`
		case "sla_started":
			return "SLA started"
		case "sla_paused":
			return "SLA paused"
		case "sla_breached":
			return "SLA breached"
		case "sla_resolved":
			return "SLA resolved"
		case "sla_policy_set":
			return "SLA set"
		case "sla_policy_removed":
			return "SLA removed"
		case "pr_opened":
			return `PR #${parsed.payload.number ?? ""} opened`.trim()
		case "pr_merged":
			return `${parsed.payload.issueIdentifier ? `${parsed.payload.issueIdentifier} ` : ""}PR #${parsed.payload.number ?? ""} merged`.trim()
		case "pr_closed":
			return `PR #${parsed.payload.number ?? ""} closed`.trim()
		case "coding_agent_triggered":
			return `agent triggered for ${parsed.payload.issueIdentifier ?? "issue"}`
		case "coding_agent_running":
			return `agent started on ${parsed.payload.issueIdentifier ?? "issue"}`
		case "coding_agent_finished":
			return `agent finished ${parsed.payload.issueIdentifier ?? "issue"}`
		case "coding_agent_failed":
			return `agent failed on ${parsed.payload.issueIdentifier ?? "issue"}`
		case "coding_agent_stopped":
			return `agent stopped on ${parsed.payload.issueIdentifier ?? "issue"}`
		case "docs_proposal_started":
			return "docs proposal started"
		case "docs_proposal_ready":
			return "docs update proposed"
		case "docs_proposal_skipped":
			return parsed.payload.reason === AI_BUDGET_REACHED_MESSAGE
				? "docs proposal hit the AI spend limit"
				: "docs already covered it"
		case "custom_data_action_run":
			return `ran ${parsed.payload.action}${connectorRunPurpose(parsed.payload.source)}`
		case "ai_auto_reply_started":
			return "agent is writing a reply"
		// Rendered on the auto-reply itself, so it never reaches the timeline.
		case "ai_auto_reply_connectors":
			return "auto-reply used connectors"
		case "ai_reply_trace":
			return "agent looked things up"
		case "ai_handoff":
			return parsed.payload.reason === "agent_decision"
				? "Productlane Agent handed off"
				: "customer asked for a human"
		case "ai_notified":
			return "Productlane Agent notified the team"
		case "ai_handoff_reviewed":
			return "reviewed the handoff"
		case "ai_reply_skipped":
			if (parsed.payload.ground === "bulk") return "agent skipped bulk mail"
			return parsed.payload.ground === "no-help"
				? "agent could not help"
				: "agent sent no reply"
		case "ai_draft_skipped":
			return parsed.payload.reason === "instruction-conflict"
				? "agent stopped instead of drafting"
				: "agent described the reply instead of writing it"
		case "ai_chat_feedback":
			return parsed.payload.sentiment === "positive"
				? "customer liked the reply"
				: "customer disliked the reply"
		case "ai_resolution_changed":
			return AI_RESOLUTION_SUMMARY[parsed.payload.change]
		case "routine_run":
			return "routine ran"
		case "routine_note":
			return parsed.payload.kind === "translation"
				? "translated the message"
				: "routine left a note"
		case "ai_shadow_reply":
			return "agent wrote a preview reply"
		case "close_loop_sent":
			return "follow-up sent"
		case "close_loop_skipped":
			return "follow-up not sent"
		case "feature_request_card":
			return "agent suggested publishing the request"
		case "linear_asks_posted":
			return "Linear Asks posted in Slack"
		case "bug_inspect_requested":
			return "bug inspect started"
		case "bug_inspect_reproduced":
			return "bug inspect reproduced the bug"
		case "bug_inspect_not_reproduced":
			return "bug inspect did not reproduce the bug"
		case "bug_inspect_blocked":
			return "bug inspect did not run"
		case "bug_inspect_pr_opened":
			return "bug inspect opened a draft pull request"
		case "bug_inspect_approved":
			return "approved the fix"
		case "bug_inspect_rejected":
			return "rejected the fix"
		default: {
			// Fallback: derive summary from the event type name
			const _exhaustiveCheck: never = parsed
			return (
				(_exhaustiveCheck as { eventType: string }).eventType ?? "activity"
			).replace(/_/g, " ")
		}
	}
}
